import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Lock } from 'lucide-react';
import Logo from '../components/Logo';
import { resetPassword } from '../services/api';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import { useToast } from '../components/Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import { firstFailureMessage, isPasswordValid, PASSWORD_MIN_LENGTH } from '../lib/passwordStrength';

// Paso 2 del flujo "Olvidé mi contraseña". El usuario llega aquí desde el
// enlace del email; los parámetros `email` y `auth` (el token) vienen en la
// query string. Validamos que estén presentes, pedimos la nueva contraseña
// (dos veces) y llamamos al endpoint. Tras éxito, mostramos confirmación y
// permitimos volver al login.

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const email = params.get('email') || '';
  const auth = params.get('auth') || '';

  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const toast = useToast();

  // El enlace siempre debe traer email + auth. Si falta alguno, mostramos
  // un estado de error en lugar del formulario — no tiene sentido permitir
  // intentar el POST si sabemos que el backend lo rechazará seguro.
  const linkInvalid = useMemo(() => !email || !auth, [email, auth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isPasswordValid(pwd)) {
      setError(firstFailureMessage(pwd) || 'La contraseña no cumple los requisitos.');
      return;
    }
    if (pwd !== pwd2) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ email, newPassword: pwd, auth });
      setDone(true);
      // Dura más que la redirección (4 s) para que se siga viendo en el login.
      toast.success(FEEDBACK.passwordReset, { duration: 8000 });
      // Redirigimos al login tras unos segundos para que el usuario lea el
      // mensaje. Si quiere antes, ya hay un botón.
      setTimeout(() => navigate('/login', { replace: true }), 4000);
    } catch (err) {
      // Traducimos los errores conocidos del backend a algo más amable.
      const raw = String(err?.message || '').toLowerCase();
      if (raw.includes('token has expired') || raw.includes('invalid or expired token')) {
        setError('El enlace ha caducado o ya no es válido. Vuelve a solicitar el cambio de contraseña desde el login.');
      } else if (raw.includes('email does not match token')) {
        setError('El enlace no coincide con tu email. Asegúrate de usar el último correo recibido.');
      } else {
        setError(err?.message || 'No se pudo restablecer la contraseña.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background:
          'radial-gradient(ellipse at top right, #E0F2FE 0%, transparent 50%), radial-gradient(ellipse at bottom left, #F1F5F9 0%, transparent 50%), #F8FAFC',
      }}
    >
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-ink-muted hover:text-primary-dark text-sm font-medium mb-8 transition-colors">
          <ArrowLeft size={16} /> Volver al login
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <div className="shadow-lg shadow-primary/30 rounded-[10px]">
              <Logo size={48} variant="onLight" />
            </div>
            <span className="font-display font-bold text-2xl text-ink">TemControl</span>
          </div>
          <p className="text-sm text-ink-muted">Restablecer contraseña</p>
        </div>

        <div
          className="bg-white rounded-3xl p-8 md:p-10"
          style={{ boxShadow: '0 40px 80px -20px rgba(19, 78, 74, 0.15), 0 0 0 1px rgba(19, 78, 74, 0.04)' }}
        >
          {done ? (
            <>
              <div className="flex items-center gap-2 text-emerald-700 mb-4">
                <CheckCircle2 size={20} />
                <h2 className="font-display text-xl font-bold">Contraseña actualizada</h2>
              </div>
              <p className="text-sm text-ink-muted mb-6">
                Tu contraseña se ha cambiado correctamente. Ya puedes iniciar sesión con la nueva contraseña.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 w-full bg-primary text-white py-3 rounded-xl font-semibold text-[0.95rem] hover:bg-primary-dark transition-all"
              >
                Ir al login <ArrowRight size={18} />
              </Link>
            </>
          ) : linkInvalid ? (
            <>
              <div className="flex items-center gap-2 text-red-700 mb-4">
                <AlertCircle size={20} />
                <h2 className="font-display text-xl font-bold">Enlace inválido</h2>
              </div>
              <p className="text-sm text-ink-muted mb-6">
                El enlace que has utilizado no es válido o le faltan datos.
                Vuelve a solicitar un nuevo correo de recuperación desde la pantalla de login.
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center justify-center gap-2 w-full bg-primary text-white py-3 rounded-xl font-semibold text-[0.95rem] hover:bg-primary-dark transition-all"
              >
                Solicitar nuevo enlace <ArrowRight size={18} />
              </Link>
            </>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold mb-1">Nueva contraseña</h2>
              <p className="text-sm text-ink-muted mb-6">
                Estás restableciendo la contraseña de <span className="font-semibold text-ink">{email}</span>.
              </p>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-2">Nueva contraseña</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                    <input
                      type="password"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      placeholder="••••••••"
                      autoFocus
                      className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl text-[0.95rem] bg-slate-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10 transition"
                      required
                      minLength={PASSWORD_MIN_LENGTH}
                    />
                  </div>
                  <PasswordStrengthMeter password={pwd} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink mb-2">Confirmar contraseña</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                    <input
                      type="password"
                      value={pwd2}
                      onChange={(e) => setPwd2(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl text-[0.95rem] bg-slate-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10 transition"
                      required
                      minLength={PASSWORD_MIN_LENGTH}
                    />
                  </div>
                  {pwd2 && pwd && pwd !== pwd2 && (
                    <div className="text-[11px] text-danger mt-1.5 inline-flex items-center gap-1">
                      <AlertCircle size={11} /> Las contraseñas no coinciden.
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white py-3 rounded-xl font-semibold text-[0.95rem] flex items-center justify-center gap-2 hover:bg-primary-dark transition-all hover:shadow-lg hover:shadow-primary/30 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Guardar contraseña <ArrowRight size={18} /></>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-ink-muted text-xs mt-6">
          © {new Date().getFullYear()} TemControl · Gestión horaria
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
